import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import { FRENCH_BANKS, OTHER_BANK_VALUE } from 'src/Constants/frenchBanks';
import { CreateBankAccountDto } from 'src/Services/API/generated';
import { validateIBANFormat } from 'src/Services/Validators/validateIBANFormat';

export interface BankAccountFormValues {
  name: string;
  bankSelection: string;
  bankName: string;
  bankDetail: string;
  holderName: string;
  iban: string;
  bic: string;
}

/** Stored and validated without spaces, upper case. */
const compact = (value: string): string => value.replace(/\s+/g, '').toUpperCase();

const emptyToNull = (value: string): string | null => (value ? value : null);

const BIC_PATTERN = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

export const useCreateBankAccountForm = () => {
  const { t } = useTranslation();

  const form = useForm<
    BankAccountFormValues,
    (values: BankAccountFormValues) => CreateBankAccountDto
  >({
    initialValues: {
      name: '',
      bankSelection: '',
      bankName: '',
      bankDetail: '',
      holderName: '',
      iban: '',
      bic: '',
    },

    validate: values => {
      const errors: Record<string, string> = {};

      if (!values.name.trim()) {
        errors.name = t('common.form.errors.required', 'Ce champ est requis.');
      }

      if (values.bankSelection === OTHER_BANK_VALUE && !values.bankName.trim()) {
        errors.bankName = t('common.form.errors.required', 'Ce champ est requis.');
      }

      if (values.iban.trim() && !validateIBANFormat(compact(values.iban))) {
        errors.iban = t(
          'bankAccounts.form.errors.iban',
          'Cet IBAN n’est pas valide. Vérifiez-le sur votre RIB.'
        );
      }

      if (values.bic.trim() && !BIC_PATTERN.test(compact(values.bic))) {
        errors.bic = t(
          'bankAccounts.form.errors.bic',
          'Un BIC compte 8 ou 11 caractères, par exemple AGRIFRPP.'
        );
      }

      return errors;
    },

    transformValues: values => {
      const selectedBank = FRENCH_BANKS.find(bank => bank.name === values.bankSelection);

      return {
        name: values.name,
        bankName: values.bankName.trim() || undefined,
        bankDomain: values.bankSelection !== OTHER_BANK_VALUE ? selectedBank?.domain : undefined,
        bankDetail: values.bankDetail.trim() || undefined,
        // null, not undefined: emptying a field on edit must clear it server side.
        holderName: emptyToNull(values.holderName.trim()),
        iban: emptyToNull(compact(values.iban)),
        bic: emptyToNull(compact(values.bic)),
      };
    },
  });

  return { form };
};
